import { Grid } from '@mui/material';
import PropTypes from 'prop-types';
import React, { memo } from 'react';

import {
  TRIP_BOOKING_TYPE_CODES,
  TRIP_BOOKING_TYPE_DESCRIPTIONS,
  TRIP_BOOKING_TYPE_ICONS,
  TRIP_BOOKING_TYPE_LABELS,
} from '@/constants/trips';
import Tooltip from 'components/v2/Tooltip';

import {
  BookingTypeContainer,
  IconContainer,
  StyledTypography,
} from './BookingTypeSelector.styles';

/**
 * BookingTypeSelector
 *
 * A reusable component that renders a selectable grid of booking types.
 * Each booking type shows an icon and a label with a tooltip containing
 * its label and description.
 *
 * Props:
 * @param {string} selectedBookingType - The currently selected booking type code.
 * @param {function} onChange - Callback invoked when a booking type is selected.
 *                              Receives the selected booking type code as an argument.
 * @param {function} [getDisableBookingType] - Optional function that receives a booking
 *                              type code and returns `true` if that type should be disabled.
 *                              Default: `() => false`.
 * @param {boolean} [isLoading] - Optional flag indicating if the component is in a loading state.
 *
 * Returns:
 * @returns {React.ReactElement} A JSX fragment containing a list of <Grid item> elements,
 *                              each rendering a booking type button with tooltip.
 *
 */

const BookingTypeSelector = ({
  selectedBookingType,
  onChange,
  getDisableBookingType = () => false,
  isLoading = false,
}) => (
  <>
    {Object.values(TRIP_BOOKING_TYPE_CODES).map((bookingTypeCode) => {
      // Icon component
      const Icon = TRIP_BOOKING_TYPE_ICONS[bookingTypeCode];
      // Display label
      const bookingTypeLabel = TRIP_BOOKING_TYPE_LABELS[bookingTypeCode];
      // Tooltip description
      const bookingTypeDescription =
        TRIP_BOOKING_TYPE_DESCRIPTIONS[bookingTypeCode];
      // Disabled state
      const disabled = getDisableBookingType(bookingTypeCode);

      return (
        <Grid item xs={12} sm={3} key={bookingTypeCode}>
          <Tooltip
            title={bookingTypeLabel}
            subtitle={bookingTypeDescription}
            arrow
            placement="top"
            enterDelay={400}
            enterNextDelay={400}
          >
            <BookingTypeContainer
              $selected={bookingTypeCode === selectedBookingType}
              $disabled={disabled || isLoading}
              onClick={() => {
                if (!disabled) onChange(bookingTypeCode);
              }}
            >
              <IconContainer
                $selected={bookingTypeCode === selectedBookingType}
              >
                <Icon />
              </IconContainer>
              <StyledTypography
                $selected={bookingTypeCode === selectedBookingType}
              >
                {bookingTypeLabel}
              </StyledTypography>
            </BookingTypeContainer>
          </Tooltip>
        </Grid>
      );
    })}
  </>
);

BookingTypeSelector.propTypes = {
  selectedBookingType: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  getDisableBookingType: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default memo(BookingTypeSelector);
